import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Filter,
  FileText,
  X,
  Upload,
  Download,
  Link as LinkIcon,
  Clock,
  MapPin,
  Users,
  DollarSign,
  Calendar,
  CheckCircle,
  FileIcon,
  ExternalLink,
  Target,
  Share2,
  Loader2,
  Repeat,
  CalendarDays,
  Package,
  ShoppingCart
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get the correct download URL for attachments
const getAttachmentUrl = (attachment) => {
  if (attachment.storage_type === "cloud") {
    // Cloud storage: use /storage/{path} endpoint
    return `${API}/storage/${attachment.filename}`;
  }
  // Local storage: use /uploads/{filename} endpoint
  return `${API}/uploads/${attachment.filename}`;
};

// Helper to format date as mm/dd/yy
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "";
  try {
    // Handle YYYY-MM-DD format
    if (dateStr.includes("-") && dateStr.length >= 10) {
      const parts = dateStr.split("T")[0].split("-");
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${month}/${day}/${year.slice(-2)}`;
      }
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

// Helper to format time as AM/PM
const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return "";
  try {
    if (timeStr.includes(":")) {
      const [hours, minutes] = timeStr.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }
    return timeStr;
  } catch {
    return timeStr;
  }
};

const STATUSES = [
  { value: "planning", label: "Planning" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "on_hold", label: "On Hold" },
  { value: "complete", label: "Complete" },
];

const PRESET_TAGS = [
  "SRA",
  "Holiday",
  "Current Event",
  "Community",
  "Children",
  "Adults",
  "Seniors",
  "Summer Reading",
  "Winter Reading",
  "STEM",
  "Arts & Crafts",
  "Storytime",
  "Book Club",
  "Workshop",
  "Author Visit"
];

const initialFormData = {
  program_name: "",
  description: "",
  proposed_date: "",
  proposed_time: "",
  proposed_location: "",
  program_type: "in_person",
  staff_needed: 1,
  expected_patrons: 0,
  cost_estimate: 0,
  supply_links: [],
  related_tags: [],
  linked_goals: [],
  book_display_id: "",
  book_list: [],
  notes: "",
  // Inventory items to order
  inventory_items: [],
  // Recurrence
  recurrence: {
    type: "none",
    days_of_week: [],
    week_of_month: "first",
    day_of_week: "friday",
    day_of_month: 1,
    end_type: "occurrences",
    occurrences: 4,
    end_date: "",
    interval: 1,
    custom_dates: []
  },
  // Approval fields
  approval_date: "",
  approved_date: "",
  approved_time: "",
  approved_description: "",
  approved_by: ""
};

const RECURRENCE_TYPES = [
  { value: "none", label: "Does not repeat" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly_day_of_week", label: "Monthly (e.g., 1st Friday)" },
  { value: "monthly_day_of_month", label: "Monthly (e.g., 15th)" },
  { value: "custom", label: "Custom dates" }
];

const WEEK_OF_MONTH_OPTIONS = [
  { value: "first", label: "First" },
  { value: "second", label: "Second" },
  { value: "third", label: "Third" },
  { value: "fourth", label: "Fourth" },
  { value: "last", label: "Last" }
];

const DAYS_OF_WEEK = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" }
];

export const Proposals = ({ user, viewMode, userLookup = {} }) => {
  const [proposals, setProposals] = useState([]);
  const [bookDisplays, setBookDisplays] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [supplyLinkInput, setSupplyLinkInput] = useState({ name: "", url: "", estimated_cost: 0, qty_needed: 1, list_price: 0 });
  const [bookInput, setBookInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const fileInputRef = useRef(null);
  
  // Inventory item input state
  const [inventoryInput, setInventoryInput] = useState({ 
    name: "", 
    quantity: 1, 
    cost_per_piece: 0, 
    notes: "" 
  });
  
  // Recurrence state
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generatingRecurrence, setGeneratingRecurrence] = useState(false);
  const [editAllDialogOpen, setEditAllDialogOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  
  // Available supply overages
  const [availableOverages, setAvailableOverages] = useState([]);
  
  // Available inventory items for proposals
  const [availableInventory, setAvailableInventory] = useState([]);

  // Check if showing all data (supervisor/admin viewing all)
  const isShowingAllData = (user?.role === "supervisor" || user?.is_admin) && viewMode !== "staff";
  
  // Helper to get staff name from userLookup
  const getStaffName = (createdBy) => {
    if (!createdBy || !userLookup[createdBy]) return null;
    return userLookup[createdBy].name;
  };

  useEffect(() => {
    fetchProposals();
    fetchBookDisplays();
    fetchGoals();
    fetchAvailableOverages();
    fetchAvailableInventory();
  }, []);

  // Auto-calculate total cost estimate from inventory items
  useEffect(() => {
    const inventoryTotal = (formData.inventory_items || []).reduce(
      (sum, item) => sum + ((item.quantity || 0) * (item.cost_per_piece || 0)),
      0
    );
    // Only update if the calculated total differs from current cost_estimate
    if (inventoryTotal !== formData.cost_estimate) {
      setFormData(prev => ({ ...prev, cost_estimate: inventoryTotal }));
    }
  }, [formData.inventory_items]);

  const fetchProposals = async () => {
    try {
      const response = await axios.get(`${API}/proposals`);
      setProposals(response.data);
    } catch (error) {
      toast.error("Failed to fetch proposals");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableOverages = async () => {
    try {
      const response = await axios.get(`${API}/supplies/available-overages`);
      setAvailableOverages(response.data.available_overages || []);
    } catch (error) {
      console.error("Failed to fetch available overages");
    }
  };

  const fetchAvailableInventory = async () => {
    try {
      const response = await axios.get(`${API}/inventory/available-for-proposals`);
      setAvailableInventory(response.data || []);
    } catch (error) {
      console.error("Failed to fetch available inventory");
    }
  };

  const fetchBookDisplays = async () => {
    try {
      const response = await axios.get(`${API}/book-displays`);
      setBookDisplays(response.data);
    } catch (error) {
      console.error("Failed to fetch book displays");
    }
  };

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/goals`);
      setGoals(response.data);
    } catch (error) {
      console.error("Failed to fetch goals");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        cost_estimate: parseFloat(formData.cost_estimate) || 0,
        staff_needed: parseInt(formData.staff_needed) || 1,
        expected_patrons: parseInt(formData.expected_patrons) || 0,
      };

      if (isEditing && selectedProposal) {
        await axios.put(`${API}/proposals/${selectedProposal.id}`, submitData);
        toast.success("Proposal updated successfully");
      } else {
        await axios.post(`${API}/proposals`, submitData);
        toast.success("Proposal created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchProposals();
    } catch (error) {
      toast.error(isEditing ? "Failed to update proposal" : "Failed to create proposal");
    }
  };

  const handleApprove = async () => {
    if (!selectedProposal) return;
    try {
      const approvalData = {
        status: "approved",
        approval_date: new Date().toISOString(),
        approved_date: formData.approved_date || selectedProposal.proposed_date,
        approved_time: formData.approved_time || selectedProposal.proposed_time,
        approved_description: formData.approved_description || selectedProposal.description,
        approved_by: formData.approved_by || "Admin"
      };
      
      await axios.put(`${API}/proposals/${selectedProposal.id}`, approvalData);
      toast.success("Proposal approved and event created!");
      setApprovalDialogOpen(false);
      resetForm();
      fetchProposals();
    } catch (error) {
      toast.error("Failed to approve proposal");
    }
  };

  const handleDelete = async () => {
    if (!selectedProposal) return;
    try {
      await axios.delete(`${API}/proposals/${selectedProposal.id}`);
      toast.success("Proposal deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedProposal(null);
      fetchProposals();
    } catch (error) {
      toast.error("Failed to delete proposal");
    }
  };

  const handleEdit = (proposal) => {
    setSelectedProposal(proposal);
    setFormData({
      program_name: proposal.program_name,
      description: proposal.description,
      proposed_date: proposal.proposed_date,
      proposed_time: proposal.proposed_time || "",
      proposed_location: proposal.proposed_location,
      program_type: proposal.program_type || "in_person",
      staff_needed: proposal.staff_needed || 1,
      expected_patrons: proposal.expected_patrons || 0,
      cost_estimate: proposal.cost_estimate || 0,
      supply_links: proposal.supply_links || [],
      related_tags: proposal.related_tags || [],
      linked_goals: proposal.linked_goals || [],
      book_display_id: proposal.book_display_id || "",
      book_list: proposal.book_list || [],
      notes: proposal.notes || "",
      inventory_items: proposal.inventory_items || [],
      recurrence: proposal.recurrence || initialFormData.recurrence,
      approval_date: proposal.approval_date || "",
      approved_date: proposal.approved_date || "",
      approved_time: proposal.approved_time || "",
      approved_description: proposal.approved_description || "",
      approved_by: proposal.approved_by || ""
    });
    setIsEditing(true);
    setDialogOpen(true);
  };

  // Generate recurring instances
  const handleGenerateRecurrence = async (proposal) => {
    if (!proposal.recurrence || proposal.recurrence.type === "none") {
      toast.error("No recurrence pattern defined for this proposal");
      return;
    }
    
    setGeneratingRecurrence(true);
    try {
      const response = await axios.post(`${API}/recurrence/generate/proposals/${proposal.id}`);
      toast.success(response.data.message);
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate recurring instances");
    } finally {
      setGeneratingRecurrence(false);
      setGenerateDialogOpen(false);
    }
  };

  // Update all instances in recurrence group
  const handleUpdateAll = async () => {
    if (!selectedProposal?.recurrence_group_id || !pendingUpdate) return;
    
    try {
      await axios.put(`${API}/recurrence/update-group/proposals/${selectedProposal.recurrence_group_id}`, pendingUpdate);
      toast.success("All recurring instances updated");
      fetchProposals();
    } catch (error) {
      toast.error("Failed to update all instances");
    } finally {
      setEditAllDialogOpen(false);
      setPendingUpdate(null);
    }
  };

  // Delete all instances in recurrence group
  const handleDeleteAllRecurring = async (proposal) => {
    if (!proposal.recurrence_group_id) return;
    
    try {
      await axios.delete(`${API}/recurrence/delete-group/proposals/${proposal.recurrence_group_id}`);
      toast.success("All recurring instances deleted");
      fetchProposals();
    } catch (error) {
      toast.error("Failed to delete recurring instances");
    }
  };

  const handleExport = async (proposal) => {
    try {
      // Export to Excel file
      const response = await axios.get(`${API}/proposals/${proposal.id}/export-excel`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${proposal.program_name.replace(/\s+/g, '-')}_proposal.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Proposal exported to Excel");
    } catch (error) {
      toast.error("Failed to export proposal");
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || !selectedProposal) return;
    
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      
      try {
        await axios.post(`${API}/proposals/${selectedProposal.id}/attachments`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    fetchProposals();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!selectedProposal) return;
    try {
      await axios.delete(`${API}/proposals/${selectedProposal.id}/attachments/${attachmentId}`);
      toast.success("Attachment deleted");
      fetchProposals();
    } catch (error) {
      toast.error("Failed to delete attachment");
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedProposal(null);
    setIsEditing(false);
    setActiveTab("details");
    setSupplyLinkInput({ name: "", url: "", estimated_cost: 0, qty_needed: 1, list_price: 0 });
    setBookInput("");
    setTagInput("");
  };

  const addSupplyLink = () => {
    if (supplyLinkInput.name && supplyLinkInput.url) {
      setFormData({
        ...formData,
        supply_links: [...formData.supply_links, { 
          ...supplyLinkInput, 
          estimated_cost: parseFloat(supplyLinkInput.estimated_cost) || 0,
          qty_needed: parseInt(supplyLinkInput.qty_needed) || 1,
          qty_purchased: 0,
          list_price: parseFloat(supplyLinkInput.list_price) || 0,
          from_overage: false
        }]
      });
      setSupplyLinkInput({ name: "", url: "", estimated_cost: 0, qty_needed: 1, list_price: 0 });
    }
  };

  const addFromOverage = (overage) => {
    // Check if this overage is already added
    const alreadyAdded = formData.supply_links.some(
      link => link.name === overage.supply_name && link.from_overage
    );
    if (alreadyAdded) {
      toast.error("This supply is already added from overages");
      return;
    }
    
    setFormData({
      ...formData,
      supply_links: [...formData.supply_links, {
        name: overage.supply_name,
        url: "",
        estimated_cost: 0,
        qty_needed: Math.min(overage.available_qty, 1), // Default to 1 or max available
        qty_purchased: 0,
        list_price: 0,
        from_overage: true,
        available_qty: overage.available_qty
      }]
    });
    toast.success(`Added "${overage.supply_name}" from available overages`);
  };

  const updateOverageQty = (index, qty) => {
    const supply = formData.supply_links[index];
    const maxQty = supply.available_qty || 999;
    const newQty = Math.min(Math.max(1, parseInt(qty) || 1), maxQty);
    
    const newLinks = [...formData.supply_links];
    newLinks[index] = { ...supply, qty_needed: newQty };
    setFormData({ ...formData, supply_links: newLinks });
  };

  const removeSupplyLink = (index) => {
    setFormData({
      ...formData,
      supply_links: formData.supply_links.filter((_, i) => i !== index)
    });
  };

  const addBook = () => {
    if (bookInput.trim()) {
      setFormData({
        ...formData,
        book_list: [...formData.book_list, bookInput.trim()]
      });
      setBookInput("");
    }
  };

  const removeBook = (index) => {
    setFormData({
      ...formData,
      book_list: formData.book_list.filter((_, i) => i !== index)
    });
  };

  const toggleTag = (tag) => {
    if (formData.related_tags.includes(tag)) {
      setFormData({
        ...formData,
        related_tags: formData.related_tags.filter(t => t !== tag)
      });
    } else {
      setFormData({
        ...formData,
        related_tags: [...formData.related_tags, tag]
      });
    }
  };

  const addCustomTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !formData.related_tags.includes(trimmedTag)) {
      setFormData({
        ...formData,
        related_tags: [...formData.related_tags, trimmedTag]
      });
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setFormData({
      ...formData,
      related_tags: formData.related_tags.filter(t => t !== tag)
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      planning: "bg-purple-100 text-purple-700 border-purple-200",
      draft: "bg-stone-100 text-stone-600 border-stone-200",
      submitted: "bg-blue-50 text-blue-700 border-blue-200",
      approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
      on_hold: "bg-amber-50 text-amber-700 border-amber-200",
      complete: "bg-cyan-50 text-cyan-700 border-cyan-200"
    };
    return styles[status] || styles.draft;
  };

  const filteredProposals = proposals.filter((proposal) => {
    const matchesSearch = proposal.program_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      proposal.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || proposal.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${month}/${day}/${year}`;
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="proposals-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="proposals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
            Program Proposals
          </h1>
          <p className="text-muted-foreground mt-1">
            Create, manage, and track library program proposals
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="create-proposal-btn"
          className="btn-animate"
        >
          <Plus className="mr-2 h-4 w-4" /> New Proposal
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search proposals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-proposals"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Proposals Grid */}
      {filteredProposals.length === 0 ? (
        <Card className="border-stone-200" data-testid="no-proposals">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" strokeWidth={1.5} />
            <h3 className="font-medium text-lg">No proposals found</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {searchQuery || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "Create your first program proposal"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProposals.map((proposal, index) => (
            <Card
              key={proposal.id}
              className="card-hover border-stone-200 animate-slide-up"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`proposal-card-${proposal.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <CardTitle className="font-heading text-lg line-clamp-2">
                        {proposal.program_name}
                      </CardTitle>
                      {proposal.shared_with?.includes(user?.id) && proposal.created_by !== user?.id && (
                        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                          <Share2 className="h-3 w-3 mr-1" />
                          Shared
                        </Badge>
                      )}
                      {proposal.recurrence?.type && proposal.recurrence.type !== "none" && (
                        <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                          <Repeat className="h-3 w-3 mr-1" />
                          Recurring
                        </Badge>
                      )}
                    </div>
                    {isShowingAllData && getStaffName(proposal.created_by) && (
                      <p className="text-xs text-blue-600 mt-1">
                        Staff: {getStaffName(proposal.created_by)}
                      </p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`proposal-menu-${proposal.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleEdit(proposal)}
                        data-testid={`edit-proposal-${proposal.id}`}
                      >
                        <Pencil className="mr-2 h-4 w-4" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleExport(proposal)}
                        data-testid={`export-proposal-${proposal.id}`}
                      >
                        <Download className="mr-2 h-4 w-4" /> Export Excel
                      </DropdownMenuItem>
                      {proposal.recurrence?.type && proposal.recurrence.type !== "none" && !proposal.recurrence_group_id && (
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedProposal(proposal);
                            setGenerateDialogOpen(true);
                          }}
                          data-testid={`generate-recurrence-${proposal.id}`}
                        >
                          <CalendarDays className="mr-2 h-4 w-4" /> Generate Dates
                        </DropdownMenuItem>
                      )}
                      {proposal.recurrence_group_id && (
                        <DropdownMenuItem
                          className="text-orange-600"
                          onClick={() => handleDeleteAllRecurring(proposal)}
                          data-testid={`delete-all-recurring-${proposal.id}`}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete All Recurring
                        </DropdownMenuItem>
                      )}
                      {proposal.status === "submitted" && (
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedProposal(proposal);
                            setFormData({
                              ...formData,
                              approved_date: proposal.proposed_date,
                              approved_time: proposal.proposed_time,
                              approved_description: proposal.description
                            });
                            setApprovalDialogOpen(true);
                          }}
                          data-testid={`approve-proposal-${proposal.id}`}
                        >
                          <CheckCircle className="mr-2 h-4 w-4" /> Approve
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => {
                          setSelectedProposal(proposal);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-proposal-${proposal.id}`}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                  {proposal.description}
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span className="font-mono text-xs">{formatDateDisplay(proposal.proposed_date)} {formatTimeDisplay(proposal.proposed_time)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span className="text-xs">{proposal.proposed_location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span className="text-xs">{proposal.staff_needed} staff needed</span>
                    {proposal.expected_patrons > 0 && (
                      <span className="text-xs">• {proposal.expected_patrons} expected patrons</span>
                    )}
                  </div>
                  {proposal.cost_estimate > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <DollarSign className="h-4 w-4" />
                      <span className="font-mono text-xs">
                        ${proposal.cost_estimate.toFixed(2)}
                        {proposal.expected_patrons > 0 && (
                          <span className="text-emerald-600 ml-2">
                            (${(proposal.cost_estimate / proposal.expected_patrons).toFixed(2)}/patron)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Tags */}
                {proposal.related_tags && proposal.related_tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {proposal.related_tags.slice(0, 3).map((tag, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {proposal.related_tags.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{proposal.related_tags.length - 3}
                      </Badge>
                    )}
                  </div>
                )}
                
                {/* Attachments indicator */}
                {proposal.attachments && proposal.attachments.length > 0 && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                    <FileIcon className="h-3 w-3" />
                    <span>{proposal.attachments.length} file(s) attached</span>
                  </div>
                )}
                
                <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
                  <Badge className={getStatusBadge(proposal.status)}>
                    {proposal.status.replace("_", " ")}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {proposal.program_type === "passive" ? "Passive" : "In-Person"}
                  </span>
                </div>
                
                {/* Event link if approved */}
                {proposal.event_id && (
                  <div className="mt-2 text-xs text-emerald-600 flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" />
                    <span>Event created</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {isEditing ? "Edit Proposal" : "Create New Proposal"}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Update the proposal details" : "Fill in the details for your new program proposal"}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="schedule">
                <Repeat className="h-3 w-3 mr-1" /> Schedule
              </TabsTrigger>
              <TabsTrigger value="resources">Resources</TabsTrigger>
              <TabsTrigger value="inventory">
                <Package className="h-3 w-3 mr-1" /> Items
              </TabsTrigger>
              <TabsTrigger value="books">Books</TabsTrigger>
              {isEditing && <TabsTrigger value="files">Files</TabsTrigger>}
            </TabsList>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <TabsContent value="details" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Program Information</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="program_name">Program Name *</Label>
                      <Input
                        id="program_name"
                        value={formData.program_name}
                        onChange={(e) => setFormData({ ...formData, program_name: e.target.value })}
                        placeholder="Enter program name"
                        required
                        data-testid="proposal-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe the program..."
                        rows={4}
                        required
                        data-testid="proposal-description-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Schedule & Location</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="proposed_date">Proposed Date *</Label>
                      <Input
                        id="proposed_date"
                        type="date"
                        value={formData.proposed_date}
                        onChange={(e) => setFormData({ ...formData, proposed_date: e.target.value })}
                        required
                        data-testid="proposal-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="proposed_time">Proposed Time *</Label>
                      <Input
                        id="proposed_time"
                        type="time"
                        value={formData.proposed_time}
                        onChange={(e) => setFormData({ ...formData, proposed_time: e.target.value })}
                        required
                        data-testid="proposal-time-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="proposed_location">Location *</Label>
                      <Input
                        id="proposed_location"
                        value={formData.proposed_location}
                        onChange={(e) => setFormData({ ...formData, proposed_location: e.target.value })}
                        placeholder="e.g., Main Reading Room"
                        required
                        data-testid="proposal-location-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Program Type</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="program_type"
                        checked={formData.program_type === "passive"}
                        onCheckedChange={(checked) => setFormData({ ...formData, program_type: checked ? "passive" : "in_person" })}
                        data-testid="program-type-toggle"
                      />
                      <Label htmlFor="program_type">
                        {formData.program_type === "passive" ? "Passive Program" : "In-Person Program"}
                      </Label>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Related Tags</h3>
                  
                  {/* Selected Tags */}
                  {formData.related_tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.related_tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="default"
                          className="px-3 py-1"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-2 hover:text-red-300"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {/* Custom Tag Input */}
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="Add custom tag..."
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addCustomTag())}
                      data-testid="custom-tag-input"
                    />
                    <Button type="button" variant="outline" onClick={addCustomTag} data-testid="add-custom-tag-btn">
                      Add Tag
                    </Button>
                  </div>
                  
                  {/* Preset Tags */}
                  <p className="text-xs text-muted-foreground mb-2">Quick add from presets:</p>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_TAGS.filter(tag => !formData.related_tags.includes(tag)).map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="cursor-pointer hover:bg-muted"
                        onClick={() => toggleTag(tag)}
                        data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        + {tag}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Linked Goals Section */}
                <div className="form-section">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Linked Goals
                  </h3>
                  
                  {/* Selected Goals */}
                  {formData.linked_goals.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.linked_goals.map((goalId) => {
                        const goal = goals.find(g => g.id === goalId);
                        return goal ? (
                          <Badge
                            key={goalId}
                            className="px-3 py-1 bg-emerald-100 text-emerald-700"
                          >
                            {goal.title}
                            <button
                              type="button"
                              onClick={() => setFormData({
                                ...formData,
                                linked_goals: formData.linked_goals.filter(id => id !== goalId)
                              })}
                              className="ml-2 hover:text-red-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  
                  {/* Goals Dropdown */}
                  {goals.length > 0 ? (
                    <>
                      <p className="text-xs text-muted-foreground mb-2">Select goals to link:</p>
                      <div className="flex flex-wrap gap-2">
                        {goals.filter(goal => !formData.linked_goals.includes(goal.id)).map((goal) => (
                          <Badge
                            key={goal.id}
                            variant="outline"
                            className="cursor-pointer hover:bg-emerald-50 hover:border-emerald-300"
                            onClick={() => setFormData({
                              ...formData,
                              linked_goals: [...formData.linked_goals, goal.id]
                            })}
                            data-testid={`link-goal-${goal.id}`}
                          >
                            + {goal.title}
                          </Badge>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">No yearly goals available. Create goals in the Goals page first.</p>
                  )}
                </div>

                {isEditing && (
                  <div className="form-section">
                    <h3 className="font-medium mb-3">Status</h3>
                    <Select
                      value={formData.status || selectedProposal?.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="proposal-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                    data-testid="proposal-notes-input"
                  />
                </div>
              </TabsContent>

              <TabsContent value="schedule" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Repeat className="h-4 w-4" />
                    Recurrence Pattern
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Set up this program to repeat on a schedule
                  </p>
                  
                  {/* Recurrence Type */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="recurrence_type">Repeat</Label>
                      <Select
                        value={formData.recurrence?.type || "none"}
                        onValueChange={(value) => setFormData({
                          ...formData,
                          recurrence: { ...formData.recurrence, type: value }
                        })}
                      >
                        <SelectTrigger data-testid="recurrence-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RECURRENCE_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Weekly Options */}
                    {formData.recurrence?.type === "weekly" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                        <Label>Repeat every</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="12"
                            className="w-20"
                            value={formData.recurrence?.interval || 1}
                            onChange={(e) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, interval: parseInt(e.target.value) || 1 }
                            })}
                            data-testid="recurrence-interval"
                          />
                          <span className="text-sm text-muted-foreground">week(s) on:</span>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mt-2">
                          {DAYS_OF_WEEK.map((day) => (
                            <Badge
                              key={day.value}
                              variant={formData.recurrence?.days_of_week?.includes(day.value) ? "default" : "outline"}
                              className="cursor-pointer"
                              onClick={() => {
                                const currentDays = formData.recurrence?.days_of_week || [];
                                const newDays = currentDays.includes(day.value)
                                  ? currentDays.filter(d => d !== day.value)
                                  : [...currentDays, day.value];
                                setFormData({
                                  ...formData,
                                  recurrence: { ...formData.recurrence, days_of_week: newDays }
                                });
                              }}
                              data-testid={`day-${day.value}`}
                            >
                              {day.label.slice(0, 3)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Monthly - Day of Week Options (e.g., 1st Friday) */}
                    {formData.recurrence?.type === "monthly_day_of_week" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                        <Label>Repeat on the</Label>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Select
                            value={formData.recurrence?.week_of_month || "first"}
                            onValueChange={(value) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, week_of_month: value }
                            })}
                          >
                            <SelectTrigger className="w-32" data-testid="week-of-month-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {WEEK_OF_MONTH_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <Select
                            value={formData.recurrence?.day_of_week || "friday"}
                            onValueChange={(value) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, day_of_week: value }
                            })}
                          >
                            <SelectTrigger className="w-36" data-testid="day-of-week-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DAYS_OF_WEEK.map((day) => (
                                <SelectItem key={day.value} value={day.value}>
                                  {day.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <span className="text-sm text-muted-foreground">of each month</span>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm text-muted-foreground">Every</span>
                          <Input
                            type="number"
                            min="1"
                            max="12"
                            className="w-20"
                            value={formData.recurrence?.interval || 1}
                            onChange={(e) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, interval: parseInt(e.target.value) || 1 }
                            })}
                          />
                          <span className="text-sm text-muted-foreground">month(s)</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Monthly - Day of Month Options (e.g., 15th) */}
                    {formData.recurrence?.type === "monthly_day_of_month" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                        <Label>Repeat on day</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="31"
                            className="w-20"
                            value={formData.recurrence?.day_of_month || 1}
                            onChange={(e) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, day_of_month: parseInt(e.target.value) || 1 }
                            })}
                            data-testid="day-of-month-input"
                          />
                          <span className="text-sm text-muted-foreground">of each month (use 31 for "last day")</span>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm text-muted-foreground">Every</span>
                          <Input
                            type="number"
                            min="1"
                            max="12"
                            className="w-20"
                            value={formData.recurrence?.interval || 1}
                            onChange={(e) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, interval: parseInt(e.target.value) || 1 }
                            })}
                          />
                          <span className="text-sm text-muted-foreground">month(s)</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Custom Dates */}
                    {formData.recurrence?.type === "custom" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                        <Label>Custom Dates</Label>
                        <p className="text-xs text-muted-foreground">
                          Add specific dates for this program
                        </p>
                        <div className="flex gap-2">
                          <Input
                            type="date"
                            id="custom-date-input"
                            data-testid="custom-date-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const input = document.getElementById('custom-date-input');
                              if (input?.value) {
                                const currentDates = formData.recurrence?.custom_dates || [];
                                if (!currentDates.includes(input.value)) {
                                  setFormData({
                                    ...formData,
                                    recurrence: { ...formData.recurrence, custom_dates: [...currentDates, input.value] }
                                  });
                                }
                                input.value = '';
                              }
                            }}
                            data-testid="add-custom-date-btn"
                          >
                            Add Date
                          </Button>
                        </div>
                        {formData.recurrence?.custom_dates?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {formData.recurrence.custom_dates.map((date, idx) => (
                              <Badge key={idx} variant="secondary" className="px-3 py-1">
                                {date}
                                <button
                                  type="button"
                                  className="ml-2 hover:text-red-600"
                                  onClick={() => setFormData({
                                    ...formData,
                                    recurrence: {
                                      ...formData.recurrence,
                                      custom_dates: formData.recurrence.custom_dates.filter((_, i) => i !== idx)
                                    }
                                  })}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* End Conditions - for all recurrence types except none and custom */}
                    {formData.recurrence?.type && formData.recurrence.type !== "none" && formData.recurrence.type !== "custom" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                        <Label>End After</Label>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="radio"
                              id="end-occurrences"
                              name="end-type"
                              checked={formData.recurrence?.end_type === "occurrences"}
                              onChange={() => setFormData({
                                ...formData,
                                recurrence: { ...formData.recurrence, end_type: "occurrences" }
                              })}
                            />
                            <Label htmlFor="end-occurrences" className="font-normal">After</Label>
                            <Input
                              type="number"
                              min="1"
                              max="52"
                              className="w-20"
                              value={formData.recurrence?.occurrences || 4}
                              onChange={(e) => setFormData({
                                ...formData,
                                recurrence: { ...formData.recurrence, occurrences: parseInt(e.target.value) || 1 }
                              })}
                              disabled={formData.recurrence?.end_type !== "occurrences"}
                              data-testid="occurrences-input"
                            />
                            <span className="text-sm text-muted-foreground">occurrence(s)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            id="end-date"
                            name="end-type"
                            checked={formData.recurrence?.end_type === "end_date"}
                            onChange={() => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, end_type: "end_date" }
                            })}
                          />
                          <Label htmlFor="end-date" className="font-normal">By date</Label>
                          <Input
                            type="date"
                            className="w-40"
                            value={formData.recurrence?.end_date || ""}
                            onChange={(e) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, end_date: e.target.value }
                            })}
                            disabled={formData.recurrence?.end_type !== "end_date"}
                            data-testid="end-date-input"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Info about generating recurring instances */}
                {formData.recurrence?.type && formData.recurrence.type !== "none" && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                    <p className="font-medium flex items-center gap-2">
                      <CalendarDays className="h-4 w-4" />
                      How Recurring Programs Work
                    </p>
                    <p className="mt-1 text-blue-600">
                      After saving, use the "Generate Dates" option from the card menu to create all the recurring instances. 
                      You can then edit or delete individual instances as needed.
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="resources" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Staffing</h3>
                  <div>
                    <Label htmlFor="staff_needed">Number of Staff Needed</Label>
                    <Input
                      id="staff_needed"
                      type="number"
                      min="1"
                      value={formData.staff_needed}
                      onChange={(e) => setFormData({ ...formData, staff_needed: parseInt(e.target.value) || 1 })}
                      data-testid="staff-needed-input"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Budget & Attendance</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="expected_patrons">Expected Patrons</Label>
                      <Input
                        id="expected_patrons"
                        type="number"
                        min="0"
                        value={formData.expected_patrons}
                        onChange={(e) => setFormData({ ...formData, expected_patrons: parseInt(e.target.value) || 0 })}
                        data-testid="expected-patrons-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cost_estimate" className="flex items-center gap-2">
                        Total Cost Estimate ($)
                        {(formData.inventory_items?.length > 0) && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            Auto-calculated
                          </Badge>
                        )}
                      </Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="cost_estimate"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.cost_estimate}
                          onChange={(e) => setFormData({ ...formData, cost_estimate: parseFloat(e.target.value) || 0 })}
                          className={`pl-9 font-mono ${formData.inventory_items?.length > 0 ? 'bg-blue-50/50' : ''}`}
                          data-testid="cost-estimate-input"
                        />
                      </div>
                      {formData.inventory_items?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Calculated from {formData.inventory_items.length} inventory item(s)
                        </p>
                      )}
                    </div>
                  </div>
                  {formData.expected_patrons > 0 && formData.cost_estimate > 0 && (
                    <div className="mt-2 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                      <p className="text-sm text-emerald-700">
                        <strong>Cost per patron:</strong>{" "}
                        <span className="font-mono">${(formData.cost_estimate / formData.expected_patrons).toFixed(2)}</span>
                      </p>
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Supply Links</h3>
                  
                  {/* Use from Available Overages */}
                  {availableOverages.length > 0 && (
                    <div className="mb-4 p-3 border border-purple-200 rounded-lg bg-purple-50/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-700">Use from Available Overages</span>
                        <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 border-purple-200">
                          {availableOverages.length} items available
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        These supplies are leftover from previous programs and can be reused at no cost.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {availableOverages.map((overage, idx) => (
                          <Button
                            key={idx}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs border-purple-200 hover:bg-purple-100"
                            onClick={() => addFromOverage(overage)}
                            data-testid={`use-overage-${overage.supply_name}`}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            {overage.supply_name} ({overage.available_qty} avail)
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add new supply link */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                    <Input
                      placeholder="Supply name"
                      value={supplyLinkInput.name}
                      onChange={(e) => setSupplyLinkInput({ ...supplyLinkInput, name: e.target.value })}
                      data-testid="supply-name-input"
                    />
                    <Input
                      placeholder="URL"
                      value={supplyLinkInput.url}
                      onChange={(e) => setSupplyLinkInput({ ...supplyLinkInput, url: e.target.value })}
                      data-testid="supply-url-input"
                    />
                    <Input
                      type="number"
                      placeholder="Est. cost"
                      value={supplyLinkInput.estimated_cost}
                      onChange={(e) => setSupplyLinkInput({ ...supplyLinkInput, estimated_cost: parseFloat(e.target.value) || 0 })}
                      data-testid="supply-cost-input"
                    />
                    <Button type="button" variant="outline" onClick={addSupplyLink} data-testid="add-supply-btn">
                      Add Supply
                    </Button>
                  </div>
                  {formData.supply_links.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {formData.supply_links.map((link, index) => (
                        <div key={index} className={`flex items-center justify-between p-2 rounded-lg ${link.from_overage ? 'bg-purple-50 border border-purple-200' : 'bg-muted/30'}`}>
                          <div className="flex items-center gap-2 flex-1">
                            {link.from_overage ? (
                              <Package className="h-4 w-4 text-purple-500" />
                            ) : (
                              <LinkIcon className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="text-sm font-medium">{link.name}</span>
                            {link.from_overage ? (
                              <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 border-purple-200">
                                From Overage
                              </Badge>
                            ) : (
                              <>
                                {link.url && (
                                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                    {link.url.slice(0, 30)}...
                                  </a>
                                )}
                                <span className="text-xs font-mono text-muted-foreground">${(link.estimated_cost || 0).toFixed(2)}</span>
                              </>
                            )}
                            {link.from_overage && (
                              <div className="flex items-center gap-1 ml-2">
                                <span className="text-xs text-muted-foreground">Qty:</span>
                                <Input
                                  type="number"
                                  min="1"
                                  max={link.available_qty}
                                  value={link.qty_needed}
                                  onChange={(e) => updateOverageQty(index, e.target.value)}
                                  className="h-6 w-16 text-xs"
                                />
                                <span className="text-xs text-muted-foreground">/ {link.available_qty}</span>
                              </div>
                            )}
                          </div>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeSupplyLink(index)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="inventory" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4" />
                    Items from Inventory
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Select existing inventory items to reserve for this program. Items will be reserved when the proposal is approved.
                  </p>
                  
                  {/* Select from Existing Inventory */}
                  {availableInventory.length > 0 && (
                    <div className="mb-4 p-3 border border-green-200 rounded-lg bg-green-50/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">Available Inventory Items</span>
                        <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-200">
                          {availableInventory.length} items
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        These supplies are available in inventory and can be reserved for your program.
                      </p>
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {availableInventory.filter(inv => 
                          !formData.inventory_items?.some(item => item.inventory_item_id === inv.id)
                        ).map((inv) => (
                          <div
                            key={inv.id}
                            className="flex items-center justify-between p-2 bg-white rounded border hover:border-green-300 cursor-pointer"
                            onClick={() => {
                              const newItem = {
                                id: `inv-${inv.id}`,
                                name: inv.name,
                                quantity: 1,
                                cost_per_piece: inv.cost_per_piece || 0,
                                notes: inv.location ? `Location: ${inv.location}` : "",
                                inventory_item_id: inv.id,
                                available_quantity: inv.available_quantity,
                                from_inventory: true
                              };
                              setFormData({
                                ...formData,
                                inventory_items: [...(formData.inventory_items || []), newItem]
                              });
                              toast.success(`Added "${inv.name}" from inventory`);
                            }}
                            data-testid={`select-inventory-${inv.id}`}
                          >
                            <div className="flex-1">
                              <p className="text-sm font-medium">{inv.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {inv.available_quantity} available {inv.location && `• ${inv.location}`}
                              </p>
                            </div>
                            <Button type="button" variant="ghost" size="sm" className="h-7">
                              <Plus className="h-3 w-3 mr-1" /> Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <Separator className="my-4" />
                  
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Order New Items
                  </h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add new items that need to be ordered. These will become pending inventory items when approved.
                  </p>
                  
                  {/* Add Item Form */}
                  <div className="p-4 border rounded-lg bg-muted/20 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label htmlFor="inv-name">Item Name</Label>
                        <Input
                          id="inv-name"
                          value={inventoryInput.name}
                          onChange={(e) => setInventoryInput({ ...inventoryInput, name: e.target.value })}
                          placeholder="e.g., Craft supplies, Markers"
                          data-testid="inventory-item-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inv-qty">Quantity</Label>
                        <Input
                          id="inv-qty"
                          type="number"
                          min="1"
                          value={inventoryInput.quantity}
                          onChange={(e) => setInventoryInput({ ...inventoryInput, quantity: parseInt(e.target.value) || 1 })}
                          data-testid="inventory-item-qty"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inv-cost">Cost per Item ($)</Label>
                        <Input
                          id="inv-cost"
                          type="number"
                          min="0"
                          step="0.01"
                          value={inventoryInput.cost_per_piece}
                          onChange={(e) => setInventoryInput({ ...inventoryInput, cost_per_piece: parseFloat(e.target.value) || 0 })}
                          data-testid="inventory-item-cost"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="inv-notes">Notes (optional)</Label>
                        <Input
                          id="inv-notes"
                          value={inventoryInput.notes}
                          onChange={(e) => setInventoryInput({ ...inventoryInput, notes: e.target.value })}
                          placeholder="Link to product, specifications, etc."
                          data-testid="inventory-item-notes"
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        if (!inventoryInput.name.trim()) {
                          toast.error("Please enter an item name");
                          return;
                        }
                        const newItem = {
                          id: `temp-${Date.now()}`,
                          name: inventoryInput.name.trim(),
                          quantity: inventoryInput.quantity,
                          cost_per_piece: inventoryInput.cost_per_piece,
                          notes: inventoryInput.notes,
                          inventory_item_id: null,
                          received: false
                        };
                        setFormData({
                          ...formData,
                          inventory_items: [...(formData.inventory_items || []), newItem]
                        });
                        setInventoryInput({ name: "", quantity: 1, cost_per_piece: 0, notes: "" });
                      }}
                      data-testid="add-inventory-item-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add Item
                    </Button>
                  </div>
                  
                  {/* Items List */}
                  {formData.inventory_items?.length > 0 && (
                    <div className="space-y-2 mt-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium">Items Added ({formData.inventory_items.length})</h4>
                        <p className="text-sm text-muted-foreground">
                          Total: ${formData.inventory_items.reduce((sum, item) => sum + (item.quantity * item.cost_per_piece), 0).toFixed(2)}
                        </p>
                      </div>
                      {formData.inventory_items.map((item, index) => (
                        <div key={item.id || index} className={`flex items-center justify-between p-3 border rounded-lg ${item.from_inventory ? 'bg-green-50 border-green-200' : 'bg-white'}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{item.name}</p>
                              {item.from_inventory && (
                                <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-200">
                                  From Inventory
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                              {item.from_inventory ? (
                                <>
                                  <div className="flex items-center gap-1">
                                    <span>Reserve:</span>
                                    <Input
                                      type="number"
                                      min="1"
                                      max={item.available_quantity || 999}
                                      value={item.quantity}
                                      onChange={(e) => {
                                        const newQty = Math.min(
                                          Math.max(1, parseInt(e.target.value) || 1),
                                          item.available_quantity || 999
                                        );
                                        const newItems = [...formData.inventory_items];
                                        newItems[index] = { ...item, quantity: newQty };
                                        setFormData({ ...formData, inventory_items: newItems });
                                      }}
                                      className="h-6 w-16 text-xs"
                                    />
                                    <span>/ {item.available_quantity} available</span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="flex items-center gap-1">
                                    <span>Qty:</span>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={item.quantity}
                                      onChange={(e) => {
                                        const newQty = Math.max(1, parseInt(e.target.value) || 1);
                                        const newItems = [...formData.inventory_items];
                                        newItems[index] = { ...item, quantity: newQty };
                                        setFormData({ ...formData, inventory_items: newItems });
                                      }}
                                      className="h-6 w-16 text-xs"
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span>$</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.cost_per_piece}
                                      onChange={(e) => {
                                        const newCost = Math.max(0, parseFloat(e.target.value) || 0);
                                        const newItems = [...formData.inventory_items];
                                        newItems[index] = { ...item, cost_per_piece: newCost };
                                        setFormData({ ...formData, inventory_items: newItems });
                                      }}
                                      className="h-6 w-20 text-xs"
                                    />
                                    <span>/ea</span>
                                  </div>
                                  <span className="font-medium text-primary">
                                    = ${((item.quantity || 0) * (item.cost_per_piece || 0)).toFixed(2)}
                                  </span>
                                </>
                              )}
                            </div>
                            {item.notes && <p className="text-xs text-muted-foreground mt-1">{item.notes}</p>}
                            {item.inventory_item_id && !item.from_inventory && (
                              <Badge variant="outline" className="mt-1 text-xs">
                                <Package className="h-3 w-3 mr-1" /> Created in Inventory
                              </Badge>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                inventory_items: formData.inventory_items.filter((_, i) => i !== index)
                              });
                            }}
                            data-testid={`remove-inventory-item-${index}`}
                          >
                            <X className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Info box */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 mt-4">
                    <p className="font-medium flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      How Inventory Integration Works
                    </p>
                    <ul className="mt-2 text-blue-600 space-y-1 text-xs">
                      <li>• <strong>From Inventory:</strong> Items selected from existing inventory will be reserved when the proposal is approved.</li>
                      <li>• <strong>New Items:</strong> Items you add manually will be created as pending inventory items when approved.</li>
                      <li>• Reserved quantities are automatically deducted from available stock.</li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="books" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Link to Book Display</h3>
                  <Select
                    value={formData.book_display_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, book_display_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="book-display-select">
                      <SelectValue placeholder="Select a book display (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {bookDisplays.map((display) => (
                        <SelectItem key={display.id} value={display.id}>
                          {display.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Book List</h3>
                  <div className="flex gap-2 mb-2">
                    <Input
                      placeholder="Add a book title"
                      value={bookInput}
                      onChange={(e) => setBookInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addBook())}
                      data-testid="book-list-input"
                    />
                    <Button type="button" variant="outline" onClick={addBook} data-testid="add-book-btn">
                      Add
                    </Button>
                  </div>
                  {formData.book_list.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.book_list.map((book, index) => (
                        <Badge key={index} variant="secondary" className="px-3 py-1">
                          {book}
                          <button type="button" onClick={() => removeBook(index)} className="ml-2 hover:text-red-600">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {isEditing && (
                <TabsContent value="files" className="space-y-4">
                  <div className="form-section">
                    <h3 className="font-medium mb-3">File Attachments</h3>
                    <div className="space-y-4">
                      <div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                          data-testid="file-upload-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          data-testid="upload-file-btn"
                        >
                          <Upload className="mr-2 h-4 w-4" /> Upload Files
                        </Button>
                        <p className="text-xs text-muted-foreground mt-1">All file types accepted</p>
                      </div>
                      
                      {selectedProposal?.attachments && selectedProposal.attachments.length > 0 && (
                        <div className="space-y-2">
                          {selectedProposal.attachments.map((attachment) => (
                            <div key={attachment.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                              <div className="flex items-center gap-2">
                                <FileIcon className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="text-sm font-medium">{attachment.original_name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {(attachment.file_size / 1024).toFixed(1)} KB • {formatDate(attachment.uploaded_at)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(getAttachmentUrl(attachment), '_blank')}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAttachment(attachment.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-proposal-btn">
                  {isEditing ? "Update Proposal" : "Create Proposal"}
                </Button>
              </DialogFooter>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Approve Proposal</DialogTitle>
            <DialogDescription>
              Confirm the event details. This will create a participation event.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="font-medium">{selectedProposal?.program_name}</p>
              <p className="text-sm text-muted-foreground mt-1">{selectedProposal?.description}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="approved_date">Event Date</Label>
                <Input
                  id="approved_date"
                  type="date"
                  value={formData.approved_date}
                  onChange={(e) => setFormData({ ...formData, approved_date: e.target.value })}
                  data-testid="approved-date-input"
                />
              </div>
              <div>
                <Label htmlFor="approved_time">Event Time</Label>
                <Input
                  id="approved_time"
                  type="time"
                  value={formData.approved_time}
                  onChange={(e) => setFormData({ ...formData, approved_time: e.target.value })}
                  data-testid="approved-time-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="approved_description">Event Description</Label>
              <Textarea
                id="approved_description"
                value={formData.approved_description}
                onChange={(e) => setFormData({ ...formData, approved_description: e.target.value })}
                rows={3}
                data-testid="approved-description-input"
              />
            </div>
            
            <div>
              <Label htmlFor="approved_by">Approved By</Label>
              <Input
                id="approved_by"
                value={formData.approved_by}
                onChange={(e) => setFormData({ ...formData, approved_by: e.target.value })}
                placeholder="Your name"
                data-testid="approved-by-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setApprovalDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} data-testid="confirm-approve-btn">
              <CheckCircle className="mr-2 h-4 w-4" /> Approve & Create Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Proposal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedProposal?.program_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-proposal"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
